import type {
  AdminPaymentOrderDetail,
  AdminPaymentOrderListQuery,
  AdminPaymentOrderListResult,
  AppConfig,
  AppRecord,
  AppStatus,
  CreateAppInput,
  CreateAppResult,
  CredentialMetadata,
  CredentialReveal,
  PayChannel,
  PayChannelConfig,
  PaymentConfig,
  UpsertPaymentConfigInput,
  UpdateAppInput,
  UpsertPayChannelInput,
  CreatePaymentRefundInput,
  PaymentRefund,
  PaymentReconciliationBatch,
  PaymentReconciliationQuery,
  PaymentStatementBatch,
  PaymentStatementImportInput,
  PaymentStatementWorkspace,
  PaymentStatementJob,
  TriggerPaymentStatementJobInput,
  UpdatePaymentDifferenceInput,
  PaymentReconciliationDifference,
  PaymentWebhookEvent,
  PaymentWebhookEventDetail,
  PaymentWebhookEventListQuery,
  PaymentWebhookEventListResult,
  WechatLoginSettings,
  UpsertWechatLoginSettings,
} from "@yahshi/contracts";

export type AppListFilters = {
  keyword: string;
  status: AppStatus | "all";
  page?: number;
  pageSize?: number;
};

export type AppListResult = {
  items: AppRecord[];
  total: number;
  activeTotal: number;
};

export type ConfigUpsertInput = Omit<AppConfig, "key"> & { key: string };

export interface M1AppApi {
  listApps(filters: AppListFilters): Promise<AppListResult>;
  getApp(appId: string): Promise<AppRecord>;
  createApp(input: CreateAppInput): Promise<CreateAppResult>;
  updateApp(appId: string, input: UpdateAppInput): Promise<AppRecord>;
  deleteApp(appId: string): Promise<void>;
  getCredential(appId: string): Promise<CredentialMetadata>;
  resetCredential(appId: string): Promise<CredentialReveal>;
  getWechatLoginSettings(appId: string): Promise<WechatLoginSettings>;
  upsertWechatLoginSettings(appId: string, input: UpsertWechatLoginSettings): Promise<WechatLoginSettings>;
  listConfigs(appId: string): Promise<AppConfig[]>;
  upsertConfig(appId: string, input: ConfigUpsertInput): Promise<AppConfig>;
  deleteConfig(appId: string, key: string): Promise<void>;
  listPayChannels(appId: string): Promise<PayChannelConfig[]>;
  upsertPayChannel(appId: string, input: UpsertPayChannelInput): Promise<PayChannelConfig>;
  deletePayChannel(appId: string, channel: PayChannel): Promise<void>;
  listPaymentOrders(query: AdminPaymentOrderListQuery): Promise<AdminPaymentOrderListResult>;
  getPaymentOrder(orderId: string): Promise<AdminPaymentOrderDetail>;
  syncPaymentOrder(orderId: string): Promise<void>;
  syncPaymentRefund(orderId: string, refundId: string): Promise<void>;
  getPaymentReconciliation(query: PaymentReconciliationQuery): Promise<PaymentReconciliationBatch>;
  getPaymentStatementWorkspace(query: PaymentReconciliationQuery): Promise<PaymentStatementWorkspace>;
  importPaymentStatement(input: PaymentStatementImportInput): Promise<PaymentStatementBatch>;
  rerunPaymentStatement(batchId: string): Promise<PaymentStatementBatch>;
  triggerPaymentStatementJob(input: TriggerPaymentStatementJobInput): Promise<PaymentStatementJob>;
  updatePaymentDifference(differenceId: string, input: UpdatePaymentDifferenceInput): Promise<PaymentReconciliationDifference>;
  listPaymentWebhookEvents(query: PaymentWebhookEventListQuery): Promise<PaymentWebhookEventListResult>;
  getPaymentWebhookEvent(eventId: string): Promise<PaymentWebhookEventDetail>;
  retryPaymentWebhookEvent(eventId: string): Promise<PaymentWebhookEvent>;
  createPaymentRefund(orderId: string, input: CreatePaymentRefundInput): Promise<PaymentRefund>;
  listPaymentConfigs(): Promise<PaymentConfig[]>;
  upsertPaymentConfig(id: string | undefined, input: UpsertPaymentConfigInput): Promise<PaymentConfig>;
  deletePaymentConfig(id: string): Promise<void>;
  linkPaymentConfig(appId: string, configId: string): Promise<void>;
  unlinkPaymentConfig(appId: string, configId: string): Promise<void>;
}

export class M1ApiError extends Error {
  readonly code: string;
  readonly fields: Record<string, string> | undefined;

  constructor(code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = "M1ApiError";
    this.code = code;
    this.fields = fields;
  }
}
